import React, { useState } from 'react';
import { Link } from 'react-scroll';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { CheckCircle2, Leaf, Heart, Globe, Menu, X } from 'lucide-react';
import { toast } from 'sonner';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    city: '',
    interest: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast.success('Thank you for your interest! We\'ll be in touch soon.');
    setFormData({
      fullName: '',
      email: '',
      city: '',
      interest: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      interest: value
    });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FAF9F7]/95 backdrop-blur-sm border-b border-[#E8C4AD]">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="text-2xl font-serif font-bold text-[#D47B5E]">
              FashionClarity
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <Link
                to="how-it-works"
                smooth={true}
                duration={500}
                className="text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer"
              >
                How it works
              </Link>
              <Link
                to="focus-areas"
                smooth={true}
                duration={500}
                className="text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer"
              >
                Focus areas
              </Link>
              <Link
                to="pilot"
                smooth={true}
                duration={500}
                className="text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer"
              >
                Pilot
              </Link>
              <Link
                to="faq"
                smooth={true}
                duration={500}
                className="text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer"
              >
                FAQ
              </Link>
              <a
                href="/demo"
                className="px-4 py-2 bg-[#D47B5E] text-white rounded-sm hover:bg-[#B8614A] transition-colors"
              >
                Demo
              </a>
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-[#D47B5E] hover:bg-[#F9E5D9] transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4 border-t border-[#E8C4AD]">
              <Link
                to="how-it-works"
                smooth={true}
                duration={500}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer py-2"
              >
                How it works
              </Link>
              <Link
                to="focus-areas"
                smooth={true}
                duration={500}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer py-2"
              >
                Focus areas
              </Link>
              <Link
                to="pilot"
                smooth={true}
                duration={500}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer py-2"
              >
                Pilot
              </Link>
              <Link
                to="faq"
                smooth={true}
                duration={500}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-[#2C2C2C] hover:text-[#D47B5E] transition-colors cursor-pointer py-2"
              >
                FAQ
              </Link>
              <a
                href="/demo"
                className="block px-4 py-2 bg-[#D47B5E] text-white rounded-sm hover:bg-[#B8614A] transition-colors text-center"
              >
                Demo
              </a>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1713261749235-901031087be6"
            alt="Elegant sustainable fashion"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAF9F7]/80 via-[#FAF9F7]/90 to-[#FAF9F7]"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-light text-[#D47B5E] mb-8 leading-tight">
            Clarity for what you wear, and why.
          </h1>
          <p className="text-xl sm:text-2xl text-[#2C2C2C] mb-12 leading-relaxed max-w-3xl mx-auto">
            FashionClarity helps people translate intent, cultural identity, sustainability preferences and climate context into a structured design brief for outfits and bespoke garments.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="pilot" smooth={true} duration={500}>
              <Button className="px-8 py-6 bg-[#D47B5E] text-white hover:bg-[#B8614A] transition-all text-lg">
                Join the Pilot
              </Button>
            </Link>
            <Link to="how-it-works" smooth={true} duration={500}>
              <Button variant="outline" className="px-8 py-6 border-2 border-[#D47B5E] text-[#D47B5E] hover:bg-[#D47B5E] hover:text-white transition-all text-lg">
                How it works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-8 bg-[#F9E5D9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#B8927C] text-lg font-medium">
            Pilot launching from Sutton, London. Limited places available.
          </p>
          <p className="text-[#B8927C] text-sm mt-2">
            Built from real bespoke studio practice.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-serif font-light text-[#D47B5E] text-center mb-16">
            How it works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E8B298] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold mb-4 text-[#D47B5E]">Tell us the occasion</h3>
              <p className="text-[#2C2C2C] leading-relaxed">
                Share what you want to communicate and the context of your needs.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E8B298] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold mb-4 text-[#D47B5E]">Select your preferences</h3>
              <p className="text-[#2C2C2C] leading-relaxed">
                Choose cultural references, climate needs, and sustainability preferences.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E8B298] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold mb-4 text-[#D47B5E]">Receive your brief</h3>
              <p className="text-[#2C2C2C] leading-relaxed">
                Get a structured design brief with colour logic, textile guidance, and silhouette strategy.
              </p>
            </div>
          </div>

          {/* Sample Design Brief */}
          <Card className="max-w-md mx-auto bg-white border-[#E8C4AD] shadow-lg">
            <CardHeader>
              <CardTitle className="font-serif text-2xl text-[#D47B5E]">Sample Design Brief</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="font-semibold text-[#D47B5E]">Colour logic:</span>{' '}
                <span className="text-[#2C2C2C]">Warm earth tones for grounding presence...</span>
              </div>
              <div>
                <span className="font-semibold text-[#D47B5E]">Textile:</span>{' '}
                <span className="text-[#2C2C2C]">Natural fibres, breathable weaves...</span>
              </div>
              <div>
                <span className="font-semibold text-[#D47B5E]">Silhouette:</span>{' '}
                <span className="text-[#2C2C2C]">Structured shoulders, flowing lines...</span>
              </div>
              <div>
                <span className="font-semibold text-[#D47B5E]">Layering:</span>{' '}
                <span className="text-[#2C2C2C]">Transitional pieces for UK climate...</span>
              </div>
              <div>
                <span className="font-semibold text-[#D47B5E]">Sustainability notes:</span>{' '}
                <span className="text-[#2C2C2C]">Multi-season adaptability...</span>
              </div>
              <div>
                <span className="font-semibold text-[#D47B5E]">Rationale:</span>{' '}
                <span className="text-[#2C2C2C]">Balances professional presence with cultural authenticity...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Focus Areas */}
      <section id="focus-areas" className="py-20 bg-[#F9E5D9] px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-serif font-light text-[#D47B5E] text-center mb-16">
            Three Market Focus
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-[#E8C4AD] shadow-md hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-[#E8B298] rounded-full flex items-center justify-center mb-4">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl text-[#D47B5E]">Sustainable Fashion</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#2C2C2C] leading-relaxed">
                  Focus on intentional wardrobes, durability, and multi-use garments that reduce environmental impact.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-[#E8C4AD] shadow-md hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-[#E8B298] rounded-full flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl text-[#D47B5E]">Confidence & Lifestyle</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#2C2C2C] leading-relaxed">
                  Reduce decision fatigue and support presentation clarity through structured design guidance.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-[#E8C4AD] shadow-md hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-[#E8B298] rounded-full flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-xl text-[#D47B5E]">African Diaspora Cultural Fashion</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#2C2C2C] leading-relaxed">
                  Respectful cultural intelligence and UK climate adaptation for diaspora communities.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why FashionClarity */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-serif font-light text-[#D47B5E] text-center mb-16">
            Why FashionClarity
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <CheckCircle2 className="w-6 h-6 text-[#E8B298] mt-1 mr-4 flex-shrink-0" />
                <p className="text-[#2C2C2C] leading-relaxed">Explainable recommendations (not a black box)</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="w-6 h-6 text-[#E8B298] mt-1 mr-4 flex-shrink-0" />
                <p className="text-[#2C2C2C] leading-relaxed">Designed for UK climate and real life contexts</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start">
                <CheckCircle2 className="w-6 h-6 text-[#E8B298] mt-1 mr-4 flex-shrink-0" />
                <p className="text-[#2C2C2C] leading-relaxed">Supports intentional purchasing and wardrobe longevity</p>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="w-6 h-6 text-[#E8B298] mt-1 mr-4 flex-shrink-0" />
                <p className="text-[#2C2C2C] leading-relaxed">Cultural intelligence with respectful adaptation rules</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pilot Invitation */}
      <section id="pilot" className="py-20 bg-[#F9E5D9] px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-serif font-light text-[#D47B5E] text-center mb-12">
            Join Our Pilot Program
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-lg text-[#2C2C2C] leading-relaxed mb-8">
                Our pilot is designed for clients, stylists, small retailers, and diaspora communities who value intentional fashion choices backed by structured design intelligence.
              </p>
              
              <div className="space-y-6 mb-8">
                <div>
                  <h4 className="font-semibold text-[#D47B5E] mb-3">Pilot participants receive:</h4>
                  <ul className="text-[#2C2C2C] space-y-2">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Early access to the platform</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>1 free comprehensive design brief</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Option to provide feedback and shape the service</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-[#D47B5E] mb-2">What we collect:</h4>
                  <p className="text-[#2C2C2C] text-sm leading-relaxed">
                    Anonymous preference inputs and feedback to improve our service. 
                    Full GDPR compliance and data protection standards apply.
                  </p>
                </div>
              </div>
            </div>
            
            <Card className="bg-white border-[#E8C4AD] shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl font-serif text-[#D47B5E]">Sign Up for Pilot</CardTitle>
                <CardDescription>Limited places available</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="fullName" className="text-[#D47B5E]">Full Name *</Label>
                    <Input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      className="mt-2 border-[#E8C4AD] focus:ring-[#E8B298]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-[#D47B5E]">Email *</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 border-[#E8C4AD] focus:ring-[#E8B298]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="city" className="text-[#D47B5E]">City *</Label>
                    <Input
                      type="text"
                      id="city"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="mt-2 border-[#E8C4AD] focus:ring-[#E8B298]"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="interest" className="text-[#D47B5E]">Interested in *</Label>
                    <Select value={formData.interest} onValueChange={handleSelectChange}>
                      <SelectTrigger className="mt-2 border-[#E8C4AD]">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sustainable-fashion">Sustainable fashion</SelectItem>
                        <SelectItem value="confidence-lifestyle">Confidence & lifestyle</SelectItem>
                        <SelectItem value="african-diaspora">African diaspora cultural fashion</SelectItem>
                        <SelectItem value="retail-partner">Retail partner</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-[#D47B5E]">Optional Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-2 border-[#E8C4AD] focus:ring-[#E8B298]"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-[#D47B5E] hover:bg-[#B8614A] text-white py-6"
                  >
                    Join the Pilot
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-serif font-light text-[#D47B5E] text-center mb-16">
            Frequently Asked Questions
          </h2>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-[#E8C4AD] rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-medium text-[#D47B5E] hover:text-[#B8614A]">
                Is this a clothing store?
              </AccordionTrigger>
              <AccordionContent className="text-[#2C2C2C] leading-relaxed pt-2">
                No, FashionClarity is a design intelligence service. We provide structured design briefs to guide your clothing choices and purchases, but we don't sell clothing directly.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="border border-[#E8C4AD] rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-medium text-[#D47B5E] hover:text-[#B8614A]">
                Do you make medical or mental health claims?
              </AccordionTrigger>
              <AccordionContent className="text-[#2C2C2C] leading-relaxed pt-2">
                No, we make no medical or mental health claims. We focus purely on design intelligence and helping you make more intentional fashion choices.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="border border-[#E8C4AD] rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-medium text-[#D47B5E] hover:text-[#B8614A]">
                What do I receive after signing up?
              </AccordionTrigger>
              <AccordionContent className="text-[#2C2C2C] leading-relaxed pt-2">
                You'll receive confirmation of your pilot participation, early platform access when available, and one complimentary structured design brief.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="border border-[#E8C4AD] rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-medium text-[#D47B5E] hover:text-[#B8614A]">
                How do you handle privacy?
              </AccordionTrigger>
              <AccordionContent className="text-[#2C2C2C] leading-relaxed pt-2">
                We collect only essential information and anonymise feedback data. Full GDPR compliance and transparent data practices guide our approach.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5" className="border border-[#E8C4AD] rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-medium text-[#D47B5E] hover:text-[#B8614A]">
                Can retailers collaborate?
              </AccordionTrigger>
              <AccordionContent className="text-[#2C2C2C] leading-relaxed pt-2">
                Yes, we welcome partnerships with retailers who align with our values of sustainability, cultural respect, and quality craftsmanship.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-6" className="border border-[#E8C4AD] rounded-lg px-6 bg-white">
              <AccordionTrigger className="text-lg font-medium text-[#D47B5E] hover:text-[#B8614A]">
                When does the pilot start?
              </AccordionTrigger>
              <AccordionContent className="text-[#2C2C2C] leading-relaxed pt-2">
                The pilot will begin in Q1 2024, with participants notified via email as we approach the launch date.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] text-[#FAF9F7] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-2xl font-serif font-bold mb-4">FashionClarity</div>
              <p className="text-[#E8B298] text-sm leading-relaxed">
                Structured design intelligence for intentional fashion choices.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-[#E8B298] mb-2">contact@fashionclarity.co.uk</p>
              <p className="text-[#E8B298]">Sutton, London</p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <div className="flex flex-col space-y-2">
                <a href="#" className="text-[#E8B298] hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-[#E8B298] hover:text-white transition-colors">Terms of Service</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-[#B8927C] pt-6 text-center text-sm text-[#E8B298]">
            © 2024 FashionClarity Ltd
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
